/**
 * Story 9.13 (AC8) — client wrappers for the three-step onboarding modal.
 *
 * The browser calls ONLY the marketplace's own same-origin `/api/*` proxies
 * (server/api/onboarding-state.js, onboarding-accept-tos.js, verify-start.js,
 * verify-check.js). It NEVER calls tph-api directly and NEVER references the
 * tph-api base URL or any token (Locked Invariant 4 / architecture §4.1).
 *
 * These proxies authenticate off the user's session, so requests must carry the
 * session cookie — hence `credentials: 'include'` (also needed in dev, where the
 * API server runs on a separate port with credentialed CORS). The bodies are
 * plain JSON, so we use `window.fetch` directly (the established pattern for the
 * JSON proxies — see util/searchTelemetry.js / util/listingEngagement.js);
 * `api.js`'s `post`/`request` only carry Transit-serialized bodies. `apiBaseUrl`
 * is reused from api.js so the dev cross-port base resolves correctly.
 *
 * Non-2xx responses reject; the caller (the modal) decides how to surface the
 * error. The verify endpoints intentionally surface their non-2xx bodies (e.g.
 * the 429 lockout body with the A6 copy), so verify wrappers return the parsed
 * body alongside the status rather than throwing on a handled 4xx.
 */
import { apiBaseUrl } from './api';

const hasFetch = () => typeof window !== 'undefined' && typeof window.fetch === 'function';

const jsonFetch = (path, { method = 'GET', body } = {}) => {
  if (!hasFetch()) {
    return Promise.reject(new Error('window.fetch is unavailable'));
  }
  const bodyMaybe = body !== undefined ? { body: JSON.stringify(body) } : {};
  return window.fetch(`${apiBaseUrl()}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...bodyMaybe,
  });
};

const parseJsonSafe = async response => {
  try {
    return await response.json();
  } catch (e) {
    return {};
  }
};

/**
 * GET /api/onboarding-state — the authoritative gate signal.
 *
 * Story 9.16: the response MAY carry an additional `operator: true` field. It is
 * added SERVER-SIDE by the proxy, and only after an httpOnly HMAC-signed marker
 * (minted solely at the Console-gated login-as callback) verifies AND its bound
 * uuid matches the session's own user. The browser never sends it and can never
 * cause it to appear — it is told, not asked. Its absence is the normal case for
 * every seller on every request. It suppresses the onboarding gate and NOTHING
 * else; `status` and `missing` stay truthful alongside it.
 *
 * @returns {Promise<{status:'complete', operator?:boolean} | {status:'incomplete', missing:string[], operator?:boolean}>}
 */
export const getOnboardingState = async () => {
  const response = await jsonFetch('/api/onboarding-state', { method: 'GET' });
  if (!response.ok) {
    throw new Error(`onboarding-state responded ${response.status}`);
  }
  return parseJsonSafe(response);
};

/**
 * POST /api/onboarding/accept-tos — records T&C acceptance. The body version is
 * advisory; the server re-stamps it from the constant. `completion_triggered` in
 * the response is advisory only — teardown is driven by a fresh onboarding-state
 * GET, never by this response (LI-1).
 * @param {string} tosVersion the marketplace TOS_VERSION_CURRENT constant
 * @returns {Promise<{status:string, completion_triggered?:boolean}>}
 */
export const acceptTos = async tosVersion => {
  const response = await jsonFetch('/api/onboarding/accept-tos', {
    method: 'POST',
    body: { tos_version: tosVersion },
  });
  if (!response.ok) {
    const err = new Error(`accept-tos responded ${response.status}`);
    err.status = response.status;
    err.body = await parseJsonSafe(response);
    throw err;
  }
  return parseJsonSafe(response);
};

// Story 4.2.2 (AC4.3) — an omitted surface stays omitted so existing callers
// (OnboardingModal) keep the proxy's 'modal_j4' default byte-unchanged; the
// inquiry-path J5 gate passes surface:'inquiry_gate_j5' explicitly.
const surfaceMaybe = surface => (surface !== undefined ? { surface } : {});

/**
 * POST /api/verify/start — sends the SMS (or voice) verification code. Returns
 * the parsed body and status so the caller can render a handled 429 lockout (A6
 * copy) without a thrown error.
 * @param {{ phone:string, channel?:'sms'|'voice', surface?:string }} params
 * @returns {Promise<{ ok:boolean, status:number, body:Object }>}
 */
export const verifyStart = async ({ phone, channel = 'sms', surface } = {}) => {
  const response = await jsonFetch('/api/verify/start', {
    method: 'POST',
    body: { phone, channel, ...surfaceMaybe(surface) },
  });
  return { ok: response.ok, status: response.status, body: await parseJsonSafe(response) };
};

/**
 * POST /api/verify/check — checks the 6-digit code. On an approved check tph-api
 * has written phoneVerifiedAt. Returns parsed body + status for handled 4xx
 * (invalid_code 400, locked_out 429).
 * @param {{ phone:string, code:string, surface?:string }} params
 * @returns {Promise<{ ok:boolean, status:number, body:Object }>}
 */
export const verifyCheck = async ({ phone, code, surface } = {}) => {
  const response = await jsonFetch('/api/verify/check', {
    method: 'POST',
    body: { phone, code, ...surfaceMaybe(surface) },
  });
  return { ok: response.ok, status: response.status, body: await parseJsonSafe(response) };
};
